import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import AddServiceStaffDialog from './AddServiceStaffDialog';
import EditServiceStaffDialog from './EditServiceStaffDialog';
import api from '../utils/api';
import './ServiceStaffs.css';

const ServiceStaffs = () => {
  const { serviceId, subunitId } = useParams();
  const location = useLocation();
  const [subunit, setSubunit] = useState(location.state?.subunit || null);
  const [service, setService] = useState(location.state?.service || null);
  
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [subunits, setSubunits] = useState([]);
  const [draggedStaff, setDraggedStaff] = useState(null);
  const [dragOverStaff, setDragOverStaff] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [contextResolved, setContextResolved] = useState(!!location.state?.subunit);

  const fetchStaffs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/service-staffs?dept_ref=${subunitId}`);
      
      if (response.data.success) {
        // Sort staff by priority if available, otherwise by name
        const sortedStaffs = response.data.data.sort((a, b) => {
          if (a.priority !== undefined && b.priority !== undefined) {
            return a.priority - b.priority;
          }
          return a.name.localeCompare(b.name);
        });
        setStaffs(sortedStaffs);
      } else {
        throw new Error(response.data.message || 'Failed to fetch staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  }, [subunitId]);

  const fetchSubunits = useCallback(async () => {
    try {
      const response = await api.get(`/service-subunits?administration_ref=${serviceId}`);
      
      if (response.data.success) {
        setSubunits(response.data.data);
        const matchedSubunit = response.data.data.find(item => item._id === subunitId);
        if (matchedSubunit) {
          setSubunit(matchedSubunit);
        }
        setContextResolved(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch subunits');
      setContextResolved(true);
      setLoading(false);
    }
  }, [serviceId, subunitId]);

  const fetchService = useCallback(async () => {
    try {
      const response = await api.get('/services');
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch service');
      }

      const matchedService = response.data.data.find(item => item._id === serviceId);
      if (matchedService) {
        setService(matchedService);
      } else {
        throw new Error('Service not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch service');
    }
  }, [serviceId]);

  useEffect(() => {
    if (!service && serviceId) {
      fetchService();
    }
  }, [service, serviceId, fetchService]);

  useEffect(() => {
    if (serviceId) {
      fetchSubunits();
    }
  }, [serviceId, fetchSubunits]);

  useEffect(() => {
    if (subunit) {
      fetchStaffs();
    } else if (contextResolved) {
      setLoading(false);
      setError('Service subunit not found');
    }
  }, [contextResolved, subunit, fetchStaffs]);

  const handleAddStaff = async (staffData) => {
    try {
      if (!subunit?._id) {
        throw new Error('Service subunit not found. Please reload and try again.');
      }

      const data = {
        ...staffData,
        dept_ref: subunit._id,
        service_ref: service?._id || serviceId
      };
      
      const response = await api.post('/service-staffs', data);

      if (response.data.success) {
        setStaffs(prevStaffs => {
          const updated = [...prevStaffs, response.data.data];
          return updated.sort((a, b) => {
            if (a.priority !== undefined && b.priority !== undefined) {
              return a.priority - b.priority;
            }
            return a.name.localeCompare(b.name);
          });
        });
        setShowAddDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to create staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create staff');
    }
  };

  const handleEditStaff = async (id, staffData) => {
    try {
      const response = await api.put(`/service-staffs/${id}`, staffData);

      if (response.data.success) {
        setStaffs(prevStaffs => {
          const updated = prevStaffs.map(staff => 
            staff._id === id ? { ...staff, ...response.data.data } : staff
          );

          // Re-sort to maintain proper order after edit (by priority if available, otherwise by name)
          return updated.sort((a, b) => {
            if (a.priority !== undefined && b.priority !== undefined) {
              return a.priority - b.priority;
            }
            return a.name.localeCompare(b.name);
          });
        });
        setShowEditDialog(false);
        setSelectedStaff(null);
      } else {
        throw new Error(response.data.message || 'Failed to update staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update staff');
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    try {
      const response = await api.delete(`/service-staffs/${id}`);
      
      if (response.data.success) {
        setStaffs(staffs.filter(staff => staff._id !== id));
      } else {
        throw new Error(response.data.message || 'Failed to delete staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete staff');
    }
  };

  const openEditDialog = (staff) => {
    setSelectedStaff(staff);
    setShowEditDialog(true);
  };

  const handleDragStart = (e, staff) => {
    if (searchTerm.trim()) {
      return;
    }

    setDraggedStaff(staff);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
  };

  const handleDragOver = (e, staff) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedStaff && draggedStaff._id !== staff._id) {
      setDragOverStaff(staff._id);
    }
  };

  const handleDrop = (e, targetStaff) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();

    if (!draggedStaff || draggedStaff._id === targetStaff._id) {
      return;
    }

    const draggedIndex = staffs.findIndex(staff => staff._id === draggedStaff._id);
    const targetIndex = staffs.findIndex(staff => staff._id === targetStaff._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const newStaffs = [...staffs];
    const [removed] = newStaffs.splice(draggedIndex, 1);
    newStaffs.splice(targetIndex, 0, removed);

    const updatedStaffs = newStaffs.map((staff, index) => ({
      ...staff,
      priority: index + 1
    }));

    setStaffs(updatedStaffs);
    setDraggedStaff(null);
    setDragOverStaff(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedStaff(null);
    setDragOverStaff(null);
  };

  const sendUpdatedPriorities = useCallback(async () => {
    if (!isReordering) {
      return;
    }

    try {
      const priorities = staffs.map(staff => ({
        _id: staff._id,
        priority: staff.priority
      }));

      const response = await api.put('/service-staffs/reorder', {
        staffs: priorities
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to reorder service staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save new order');
      fetchStaffs();
    }
    finally {
      setIsReordering(false);
    }
  }, [staffs, isReordering, fetchStaffs]);

  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedPriorities();
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReordering, sendUpdatedPriorities]);

  const filteredStaffs = staffs.filter(staff =>
    staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.designation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canReorder = !searchTerm.trim() && filteredStaffs.length > 1;

  if (!subunit && !loading) return null;

  if (loading) {
    return (
      <div className="staffs-container">
        <div className="loading-spinner">Loading staff...</div>
      </div>
    );
  }

  return (
    <div className="staffs-container">
      <div className="staffs-header">
        <div className="header-left">
          <h2>Hall Staff Members - {subunit.name}</h2>
          <p className="subtitle">{subunit.nameEn}</p>
          {canReorder && (
            <p className="reorder-hint">Drag and drop staff cards to reorder them</p>
          )}
          {searchTerm.trim() && staffs.length > 1 && (
            <p className="search-hint">Clear search to reorder staffs</p>
          )}
        </div>
        <button 
          className="add-button"
          onClick={() => setShowAddDialog(true)}
        >
          + Add New Staff
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search staff by name, email, or designation..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="staffs-grid">
        {filteredStaffs.length === 0 ? (
          <div className="no-staffs">
            {searchTerm ? 'No staff found matching your search.' : 'No staff members found for this subunit.'}
          </div>
        ) : (
          <>
            {filteredStaffs.map((staff) => (
              <div
                key={staff._id}
                className={`staff-card ${draggedStaff?._id === staff._id ? 'dragging' : ''} ${dragOverStaff === staff._id ? 'drag-over' : ''}`}
                draggable={!searchTerm}
                onDragStart={(e) => handleDragStart(e, staff)}
                onDragOver={(e) => handleDragOver(e, staff)}
                onDrop={(e) => handleDrop(e, staff)}
                onDragEnd={handleDragEnd}
              >
                <div className="staff-card-header">
                  <div className="staff-avatar">
                    {staff.dp ? (
                      <img 
                        src={staff.dp} 
                        alt={staff.name} 
                        className="avatar-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span 
                      className="avatar-text"
                      style={{ display: staff.dp ? 'none' : 'flex' }}
                    >
                      {staff.name.charAt(0)}
                    </span>
                  </div>
                  <div className="drag-icon-container" title={searchTerm ? 'Clear search to reorder' : 'Drag to reorder'}>
                    <span className="drag-icon">⋮⋮</span>
                  </div>
                </div>

                <div className="staff-content">
                  <div className="staff-info">
                    <h3 className="staff-name">{staff.name}</h3>
                    {staff.nameEn && (
                      <p className="staff-name-en">{staff.nameEn}</p>
                    )}
                    <p className="staff-designation">{staff.designation}</p>
                    {staff.email && (
                      <p className="staff-email">✉️ {staff.email}</p>
                    )}
                    {staff.phone && (
                      <p className="staff-phone">📞 {staff.phone}</p>
                    )}
                    {typeof staff.priority === 'number' && (
                      <p className="staff-priority">Priority {staff.priority}</p>
                    )}
                  </div>
                </div>
                <div className="staff-card-actions">
                  <button
                    onClick={() => openEditDialog(staff)}
                    className="edit-staff-btn"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteStaff(staff._id)}
                    className="delete-staff-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {isReordering && (
              <div className="reorder-status">
                <span className="status-indicator">⏳</span>
                Updating staff order...
              </div>
            )}
          </>
        )}
      </div>

      {showAddDialog && (
        <AddServiceStaffDialog
          onClose={() => setShowAddDialog(false)}
          onAdd={handleAddStaff}
          subunitName={subunit.name}
          serviceId={service?._id || serviceId}
          subunitId={subunit._id}
        />
      )}

      {showEditDialog && selectedStaff && (
        <EditServiceStaffDialog
          staff={{
            ...selectedStaff,
            service_ref: service?._id || serviceId
          }}
          subunits={subunits}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedStaff(null);
          }}
          onEdit={handleEditStaff}
        />
      )}
    </div>
  );
};

export default ServiceStaffs;
